<template>
  <div>
    <!-- ==================== Hero ==================== -->
    <section class="hero">
      <div class="container">
        <h1>{{ $t('purchase.title') }}</h1>
        <p>{{ $t('purchase.subtitle') }}</p>
      </div>
    </section>

    <!-- ==================== Editions ==================== -->
    <section class="editions-section">
      <div class="container">
        <h2 class="section-title">{{ $t('purchase.editionsTitle') }}</h2>
        <p class="section-subtitle dark">{{ $t('purchase.editionsSubtitle') }}</p>
        <div class="edition-grid">

          <!-- 云端版 -->
          <div class="edition-card cloud">
            <div class="edition-tag">{{ $t('purchase.cloudTag') }}</div>
            <h3>{{ $t('purchase.cloudTitle') }}</h3>
            <div class="edition-price">¥0</div>
            <p class="edition-desc">{{ $t('purchase.cloudDesc') }}</p>
            <ul>
              <li>{{ $t('purchase.cloudFeature1') }}</li>
              <li>{{ $t('purchase.cloudFeature2') }}</li>
              <li>{{ $t('purchase.cloudFeature3') }}</li>
              <li>{{ $t('purchase.cloudFeature4') }}</li>
            </ul>
            <div class="edition-actions">
              <a :href="cloudDownloadUrl" target="_blank" rel="noopener" class="btn btn-primary">{{ $t('purchase.cloudDownload') }}</a>
              <a :href="cloudApiUrl" target="_blank" rel="noopener" class="btn btn-secondary">{{ $t('purchase.cloudRecharge') }}</a>
            </div>
          </div>

          <!-- 本地版 -->
          <div class="edition-card local">
            <div class="edition-tag">{{ $t('purchase.localTag') }}</div>
            <h3>{{ $t('purchase.localTitle') }}</h3>
            <div class="edition-price">{{ $t('purchase.localPrice') }}</div>
            <p class="edition-desc">{{ $t('purchase.localDesc') }}</p>
            <ul>
              <li>{{ $t('purchase.localFeature1') }}</li>
              <li>{{ $t('purchase.localFeature2') }}</li>
              <li>{{ $t('purchase.localFeature3') }}</li>
              <li>{{ $t('purchase.localFeature4') }}</li>
            </ul>
            <div class="edition-actions">
              <a href="#local-plans" class="btn btn-primary">{{ $t('purchase.localViewPlans') }}</a>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- ====================  Local Pricing Cards ==================== -->
    <section class="pricing-section" id="local-plans">
      <div class="container">
        <h2 class="section-title">{{ $t('purchase.localPlansTitle') }}</h2>
        <p class="section-subtitle dark">{{ $t('purchase.localPlansNote') }}</p>
        <div class="pricing-grid">

          <!-- 社区版 -->
          <div class="pricing-card">
            <h3>{{ $t('purchase.communityTitle') }}</h3>
            <div class="price">¥0</div>
            <p class="price-desc">{{ $t('purchase.communityDesc') }}</p>
            <ul>
              <li>{{ $t('purchase.communityFeature1') }}</li>
              <li>{{ $t('purchase.communityFeature2') }}</li>
              <li>{{ $t('purchase.communityFeature3') }}</li>
              <li>{{ $t('purchase.communityFeature4') }}</li>
              <li>{{ $t('purchase.communityFeature5') }}</li>
              <li>{{ $t('purchase.communityFeature6') }}</li>
              <li>{{ $t('purchase.communityFeature7') }}</li>
            </ul>
            <router-link to="/download" class="btn btn-primary">{{ $t('purchase.downloadNow') }}</router-link>
          </div>

          <!-- 基础版 -->
          <div class="pricing-card">
            <h3>{{ $t('purchase.basicTitle') }}</h3>
            <div class="price">
              {{ $t('purchase.basicPrice') }}
              <span>{{ $t('purchase.basicPerMonth') }}</span>
            </div>
            <div class="original-price">{{ $t('purchase.basicOriginalPrice') }}{{ $t('purchase.basicPerMonth') }}</div>
            <p class="price-desc">{{ $t('purchase.basicDesc') }}</p>
            <ul>
              <li>{{ $t('purchase.basicFeature1') }}</li>
              <li>{{ $t('purchase.basicFeature2') }}</li>
              <li>{{ $t('purchase.basicFeature3') }}</li>
              <li>{{ $t('purchase.basicFeature4') }}</li>
              <li>{{ $t('purchase.basicFeature5') }}</li>
              <li>{{ $t('purchase.basicFeature6') }}</li>
              <li>{{ $t('purchase.basicFeature7') }}</li>
            </ul>
            <button class="btn btn-primary" @click="handleBuyNow('alipay', [1, 1, 1, 4])">{{ $t('purchase.buyNow') }}</button>
          </div>

          <!-- 专业版 -->
          <div class="pricing-card featured">
            <div class="badge">{{ $t('purchase.proBadge') }}</div>
            <h3>{{ $t('purchase.proTitle') }}</h3>

            <div class="billing-toggle">
              <button :class="{ active: isMonthly }" @click="isMonthly = true">{{ $t('purchase.monthlyLabel') }}</button>
              <button :class="{ active: !isMonthly }" @click="isMonthly = false">
                {{ $t('purchase.yearlyLabel') }}
                <span class="save-tag">{{ $t('purchase.proSave') }}</span>
              </button>
            </div>

            <div class="price" v-if="isMonthly">
              {{ $t('purchase.proMonthly') }}
              <span>{{ $t('purchase.proPerMonth') }}</span>
            </div>
            <div class="price" v-else>
              {{ $t('purchase.proYearly') }}
              <span>{{ $t('purchase.proPerYear') }}</span>
            </div>
            <div class="original-price">
              {{ isMonthly ? $t('purchase.proMonthlyOriginal') : $t('purchase.proYearlyOriginal') }}{{ isMonthly ? $t('purchase.proPerMonth') : $t('purchase.proPerYear') }}
            </div>
            <p class="price-desc">{{ $t('purchase.proDesc') }}</p>
            <ul>
              <li>{{ $t('purchase.proFeature1') }}</li>
              <li>{{ $t('purchase.proFeature2') }}</li>
              <li>{{ $t('purchase.proFeature3') }}</li>
              <li>{{ $t('purchase.proFeature4') }}</li>
              <li>{{ $t('purchase.proFeature5') }}</li>
            </ul>
            <button class="btn btn-primary" @click="handleBuyNow('alipay', isMonthly ? [1, 1, 1, 1] : [1, 1, 1, 2])">{{ $t('purchase.buyNow') }}</button>
          </div>

          <!-- 企业版 -->
          <div class="pricing-card">
            <h3>{{ $t('purchase.enterpriseTitle') }}</h3>
            <div class="price">{{ $t('purchase.custom') }}</div>
            <p class="price-desc">{{ $t('purchase.enterpriseDesc') }}</p>
            <ul>
              <li>{{ $t('purchase.enterpriseFeature1') }}</li>
              <li>{{ $t('purchase.enterpriseFeature2') }}</li>
              <li>{{ $t('purchase.enterpriseFeature3') }}</li>
              <li>{{ $t('purchase.enterpriseFeature4') }}</li>
              <li>{{ $t('purchase.enterpriseFeature5') }}</li>
            </ul>
            <a href="mailto:wmx@turingevo.com" class="btn btn-secondary">{{ $t('purchase.contactSales') }}</a>
          </div>

        </div>
      </div>
    </section>

    <!-- ====================  Cloud vs Local ==================== -->
    <section class="comparison">
      <div class="container">
        <h2 class="section-title">{{ $t('purchase.editionCompareTitle') }}</h2>
        <p class="section-subtitle dark">{{ $t('purchase.editionCompareSubtitle') }}</p>
        <div class="comparison-table">
          <table>
            <thead>
              <tr>
                <th>{{ $t('purchase.compareItem') }}</th>
                <th>{{ $t('purchase.cloudTitle') }}</th>
                <th>{{ $t('purchase.localTitle') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ $t('purchase.compareModelSource') }}</td>
                <td>{{ $t('purchase.compareCloudModelSource') }}</td>
                <td>{{ $t('purchase.compareLocalModelSource') }}</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.compareCost') }}</td>
                <td>{{ $t('purchase.compareCloudCost') }}</td>
                <td>{{ $t('purchase.compareLocalCost') }}</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.compareUsageLimit') }}</td>
                <td>{{ $t('purchase.compareCloudUsage') }}</td>
                <td>{{ $t('purchase.compareLocalUsage') }}</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.compareSetup') }}</td>
                <td>{{ $t('purchase.compareCloudSetup') }}</td>
                <td>{{ $t('purchase.compareLocalSetup') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 class="section-title local-compare-title">{{ $t('purchase.compareTitle') }}</h2>
        <p class="section-subtitle dark">{{ $t('purchase.compareSubtitle') }}</p>
        <div class="comparison-table">
          <table>
            <thead>
              <tr>
                <th>{{ $t('purchase.features') }}</th>
                <th>{{ $t('purchase.communityTitle') }}</th>
                <th>{{ $t('purchase.basicTitle') }}</th>
                <th>{{ $t('purchase.proTitle') }}</th>
                <th>{{ $t('purchase.enterpriseTitle') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ $t('purchase.ragBasic') }}</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.modelSwitch') }}</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.knowledgeBaseCount') }}</td>
                <td>3</td>
                <td>5</td>
                <td>{{ $t('purchase.unlimited') }}</td>
                <td>{{ $t('purchase.unlimited') }}</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.docPerKb') }}</td>
                <td>50</td>
                <td>80</td>
                <td>{{ $t('purchase.unlimited') }}</td>
                <td>{{ $t('purchase.unlimited') }}</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.imageGen') }}</td>
                <td>{{ $t('purchase.limitImageGen') }}</td>
                <td>{{ $t('purchase.basicLimitImageGen') }}</td>
                <td>{{ $t('purchase.unlimited') }}</td>
                <td>{{ $t('purchase.unlimited') }}</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.audioTranscribe') }}</td>
                <td>{{ $t('purchase.limitAudioTranscribe') }}</td>
                <td>{{ $t('purchase.basicLimitAudioTranscribe') }}</td>
                <td>{{ $t('purchase.unlimited') }}</td>
                <td>{{ $t('purchase.unlimited') }}</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.videoAnalyze') }}</td>
                <td>{{ $t('purchase.limitVideoAnalyze') }}</td>
                <td>{{ $t('purchase.basicLimitVideoAnalyze') }}</td>
                <td>{{ $t('purchase.unlimited') }}</td>
                <td>{{ $t('purchase.unlimited') }}</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.advancedExport') }}</td>
                <td>{{ $t('purchase.limitExportGen') }}</td>
                <td>{{ $t('purchase.basicLimitExportGen') }}</td>
                <td>{{ $t('purchase.unlimited') }}</td>
                <td>{{ $t('purchase.unlimited') }}</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.agentCount') }}</td>
                <td>{{ $t('purchase.limitAgent') }}</td>
                <td>{{ $t('purchase.basicLimitAgent') }}</td>
                <td>{{ $t('purchase.unlimited') }}</td>
                <td>{{ $t('purchase.unlimited') }}</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.teamCollab') }}</td>
                <td>✗</td>
                <td>✗</td>
                <td>✗</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.sharedKb') }}</td>
                <td>✗</td>
                <td>✗</td>
                <td>✗</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.userMgmt') }}</td>
                <td>✗</td>
                <td>✗</td>
                <td>✗</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>{{ $t('purchase.support') }}</td>
                <td>{{ $t('purchase.communityTitle') }}</td>
                <td>{{ $t('purchase.basicTitle') }}</td>
                <td>{{ $t('purchase.proTitle') }}</td>
                <td>{{ $t('purchase.enterpriseTitle') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- ==================== FAQ ==================== -->
    <section class="faq-section">
      <div class="container">
        <h2 class="section-title">{{ $t('purchase.faqTitle') }}</h2>
        <div class="faq-list">
          <div class="faq-item" v-for="(faq, index) in faqs" :key="index" @click="toggleFaq(index)">
            <div class="faq-question">
              <span>{{ faq.q }}</span>
              <span class="faq-arrow" :class="{ open: faqOpen[index] }">▼</span>
            </div>
            <div class="faq-answer" v-show="faqOpen[index]">
              <p>{{ faq.a }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== CTA ==================== -->
    <section class="cta">
      <div class="container">
        <h2 class="section-title" style="color: white;">{{ $t('purchase.ctaTitle') }}</h2>
        <p style="color: rgba(255,255,255,0.8);">{{ $t('purchase.ctaSubtitle') }}</p>
        <a href="mailto:wmx@turingevo.com" class="btn btn-primary btn-lg">{{ $t('purchase.requestDemo') }}</a>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import ModalService from '../ui_server/modalService'

export default {
  name: 'Purchase',
  data() {
    return {
      isMonthly: true,
      loading: false,
      faqOpen: [false, false, false],
      cloudDownloadUrl: 'https://github.com/turingevo/Omni-note/releases/latest',
      cloudApiUrl: 'https://api.turingevo.com',
    }
  },
  computed: {
    faqs() {
      const email = 'wmx@turingevo.com'
      return [
        { q: this.$t('purchase.faqQ1'), a: this.$t('purchase.faqA1') },
        { q: this.$t('purchase.faqQ2'), a: this.$t('purchase.faqA2', { email }) },
        { q: this.$t('purchase.faqQ3'), a: this.$t('purchase.faqA3') },
      ]
    }
  },
  methods: {
    toggleFaq(index) {
      this.faqOpen[index] = !this.faqOpen[index]
    },
    async handleBuyNow(paymentMethod, categoryIndices) {
      this.loading = true
      const token = localStorage.getItem('token')
      if (!token) {
        ModalService.showError('请先登录后再进行支付', '')
        this.$router.push('/login')
        return
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      try {
        const response = await axios.post(`/api/payment/create-${paymentMethod}-order`, { categoryIndices })
        if (response.data.success) {
          const paymentData = response.data.data
          if (paymentMethod === 'alipay') {
            const newWindow = window.open('', '_blank')
            if (newWindow) {
              newWindow.document.write(paymentData.paymentUrl)
              newWindow.document.close()
              newWindow.focus()
            } else {
              ModalService.showError('无法打开支付页面，请检查浏览器弹窗设置', '')
            }
          }
        } else {
          ModalService.showError('创建订单失败: ', response.data.message)
        }
      } catch (error) {
        console.error('支付请求失败:', error)
        if (error.response && error.response.status === 401) {
          ModalService.showError('请先登录后再进行支付', '')
          this.$router.push('/login')
        } else {
          ModalService.showError('支付请求失败，请稍后重试', '')
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Hero */
.hero {
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  color: white;
  padding: 100px 20px;
  text-align: center;
}

.hero h1 {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 20px;
}

.hero p {
  font-size: 1.25rem;
  opacity: 0.9;
}

/* Editions */
.editions-section {
  padding: 80px 20px 40px;
  background: white;
}

.edition-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  max-width: 1000px;
  margin: 40px auto 0;
}

.edition-card {
  position: relative;
  background: white;
  border: 1px solid #e6e8f0;
  border-radius: 16px;
  padding: 36px 32px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  display: flex;
  flex-direction: column;
}

.edition-card.cloud {
  border: 2px solid #667eea;
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.18);
}

.edition-card h3 {
  font-size: 1.6rem;
  margin: 10px 0 12px;
  color: #222;
}

.edition-tag {
  display: inline-block;
  align-self: flex-start;
  background: #eef1ff;
  color: #4a5bd8;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.edition-card.local .edition-tag {
  background: #eef7f1;
  color: #2e7d32;
}

.edition-price {
  font-size: 2.4rem;
  font-weight: 800;
  color: #ff5f5f;
  margin-bottom: 8px;
}

.edition-desc {
  color: #666;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 20px;
}

.edition-card ul {
  list-style: none;
  padding: 0;
  margin: 0 0 28px;
  flex: 1;
}

.edition-card li {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  color: #444;
  font-size: 0.9rem;
}

.edition-card li::before {
  content: "✓ ";
  color: #4caf50;
  font-weight: bold;
}

.edition-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.edition-actions .btn {
  flex: 1;
  min-width: 160px;
}

/* Pricing Section */
.pricing-section {
  padding: 80px 20px;
  background: #f8f9fa;
  scroll-margin-top: 80px;
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  max-width: 1200px;
  margin: 40px auto 0;
  align-items: stretch;
}

.pricing-card {
  background: white;
  border-radius: 16px;
  padding: 36px 24px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.3s ease;
}

.pricing-card:hover:not(.featured) {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0,0,0,0.1);
}

.pricing-card.featured {
  transform: scale(1.05);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2);
  border: 2px solid #667eea;
}

.badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #ff5f5f, #ff9a44);
  color: white;
  padding: 6px 20px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.pricing-card h3 {
  font-size: 1.4rem;
  margin-bottom: 16px;
  color: #222;
}

.price {
  font-size: 2.5rem;
  font-weight: 800;
  color: #ff5f5f;
  margin-bottom: 4px;
}

.price span {
  font-size: 1rem;
  color: #666;
  font-weight: 400;
}

.original-price {
  font-size: 1rem;
  color: #bbb;
  text-decoration: line-through;
  margin-bottom: 12px;
}

.price-desc {
  color: #666;
  font-size: 0.85rem;
  margin-bottom: 20px;
  line-height: 1.5;
  min-height: 2.5em;
}

.pricing-card ul {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  text-align: left;
  flex: 1;
}

.pricing-card li {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  color: #444;
  font-size: 0.9rem;
}

.pricing-card li::before {
  content: "✓ ";
  color: #4caf50;
  font-weight: bold;
}

.pricing-card .btn {
  margin-top: auto;
}

/* Billing Toggle */
.billing-toggle {
  display: flex;
  background: #f0f0f5;
  border-radius: 10px;
  padding: 4px;
  margin: 0 auto 16px;
  max-width: 220px;
  gap: 4px;
}

.billing-toggle button {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #666;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.billing-toggle button.active {
  background: white;
  color: #222;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.billing-toggle .save-tag {
  display: inline-block;
  background: #e8f5e9;
  color: #2e7d32;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 0.65rem;
  font-weight: 700;
  margin-left: 4px;
  vertical-align: middle;
}

/* Comparison Table */
.comparison {
  padding: 100px 20px;
  background: white;
}

.section-subtitle.dark {
  color: #666;
}

.local-compare-title {
  margin-top: 80px;
}

.comparison-table {
  overflow-x: auto;
  margin-top: 40px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
}

.comparison-table table {
  width: 100%;
  border-collapse: collapse;
}

.comparison-table th,
.comparison-table td {
  padding: 14px 16px;
  text-align: center;
  border-bottom: 1px solid #eee;
  font-size: 0.9rem;
}

.comparison-table th {
  background: #f0f0f5;
  font-weight: 700;
  color: #333;
}

.comparison-table th:first-child,
.comparison-table td:first-child {
  text-align: left;
  white-space: nowrap;
}

.comparison-table tbody tr:hover {
  background: #f8f8ff;
}

/* FAQ */
.faq-section {
  padding: 100px 20px;
  background: #f8f9fa;
}

.faq-list {
  max-width: 700px;
  margin: 40px auto 0;
}

.faq-item {
  background: white;
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
}

.faq-item:hover {
  background: #f0f0f5;
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  font-weight: 600;
  font-size: 1rem;
  color: #333;
}

.faq-arrow {
  transition: transform 0.3s ease;
  font-size: 0.8rem;
  color: #999;
}

.faq-arrow.open {
  transform: rotate(180deg);
}

.faq-answer {
  padding: 0 24px 20px;
  color: #666;
  line-height: 1.7;
}

/* CTA */
.cta {
  padding: 100px 20px;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  color: white;
  text-align: center;
}

.cta .section-title {
  color: white !important;
}

.btn-lg {
  padding: 14px 36px;
  font-size: 1.1rem;
}

/* Responsive */
@media (max-width: 1100px) {
  .pricing-grid {
    grid-template-columns: repeat(2, 1fr);
    max-width: 700px;
  }
  .pricing-card.featured {
    transform: scale(1);
  }
}

@media (max-width: 768px) {
  .hero h1 {
    font-size: 2rem;
  }
  .edition-grid {
    grid-template-columns: 1fr;
  }
  .pricing-grid {
    grid-template-columns: 1fr;
    max-width: 400px;
  }
  .local-compare-title {
    margin-top: 56px;
  }
  .comparison-table th,
  .comparison-table td {
    padding: 10px;
    font-size: 0.8rem;
  }
}
</style>
